import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GROUP_STATUSES, GROUP_STATUS_LABEL, formatWeekdays } from '@doit/shared';
import { Plus, Search, Users } from 'lucide-react';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { DirectionTag, FormatBadge, GroupStatusBadge } from '../../components/badges';
import { useCurrentUser } from '../../api/auth';
import { useDirections, useFlows, useGroups, useProjects } from '../../api/queries';
import { GroupModal } from './GroupModal';

export function GroupsPage() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const isStaff = user.role !== 'LECTURER';

  const [flowId, setFlowId] = useState('');
  const [directionId, setDirectionId] = useState('');
  const [status, setStatus] = useState('');
  const [projectId, setProjectId] = useState('');
  const [q, setQ] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: flows } = useFlows();
  const { data: projects } = useProjects();
  const { data: directions } = useDirections();
  const { data, isLoading } = useGroups({
    flowId: flowId || undefined,
    directionId: directionId || undefined,
    status: status || undefined,
    projectId: projectId || undefined,
    q: q.trim() || undefined,
    pageSize: 100,
  });

  const groups = data?.items ?? [];

  // ლექტორს ფილტრები მხოლოდ მაშინ სჭირდება, თუ ჯგუფები რამდენიმე პროექტიდან აქვს.
  // აქტიური ფილტრი ზოლს აჩენს, რომ გაფილტრულმა (შემცირებულმა) სიამ ის არ წაშალოს.
  const anyFilter = Boolean(flowId || directionId || status || projectId || q.trim());
  const distinctProjects = new Set(groups.map((g) => g.flow.project.id)).size;
  const showFilters = isStaff || anyFilter || distinctProjects > 1;

  return (
    <>
      <PageHeader
        title="ჯგუფები"
        subtitle={
          isLoading
            ? 'იტვირთება…'
            : `${data?.total ?? 0} ჯგუფი${isStaff ? '' : ' — შენზე მიმაგრებული'}`
        }
        action={
          isStaff ? (
            <Button icon={<Plus className="size-4" />} onClick={() => setModalOpen(true)}>
              ახალი ჯგუფი
            </Button>
          ) : undefined
        }
      />

      {/* --- ფილტრები --- */}
      {showFilters && (
      <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ძებნა ჯგუფის ან კურსის სახელით…"
            className="w-full rounded-lg border border-line-strong bg-surface py-2.5 pl-9 pr-3 text-sm transition placeholder:text-ink-mute focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
          />
        </div>
        <Select
          value={flowId}
          onChange={(e) => setFlowId(e.target.value)}
          placeholder="ყველა ნაკადი"
          options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
        />
        <Select
          value={directionId}
          onChange={(e) => setDirectionId(e.target.value)}
          placeholder="ყველა მიმართულება"
          options={(directions ?? []).map((d) => ({ value: d.id, label: d.name }))}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          placeholder="ყველა სტატუსი"
          options={GROUP_STATUSES.map((s) => ({ value: s, label: GROUP_STATUS_LABEL[s] }))}
        />
      </div>

      <div className="mb-4">
        <Select
          className="max-w-48"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          placeholder="ყველა პროექტი"
          options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
      </div>
      </>
      )}

      {/* --- სია --- */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState
            icon={<Users className="size-8" />}
            title="ჯგუფები ვერ მოიძებნა"
            description={
              isStaff
                ? 'შეცვალე ფილტრები ან შექმენი ახალი ჯგუფი.'
                : 'შენზე ჯერ არცერთი ჯგუფი არ არის მიმაგრებული.'
            }
          />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {groups.map((g) => (
            <Link
              key={g.id}
              to={`/groups/${g.id}`}
              className="group rounded-[10px] border border-line bg-surface p-4 shadow-xs transition hover:border-brand-300 hover:shadow-md"
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className="tabular font-semibold tracking-tight text-ink group-hover:text-brand-700">
                  {g.name}
                </span>
                <GroupStatusBadge status={g.status} />
              </div>

              <p className="mb-3 line-clamp-2 text-[13px] text-ink-soft">{g.course.name}</p>

              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-mute">
                <DirectionTag name={g.course.direction.name} color={g.course.direction.color} />
                <span>·</span>
                <span>{g.flow.name}</span>
                <span>·</span>
                <FormatBadge format={g.format} />
              </div>

              <div className="flex items-center justify-between border-t border-line pt-3 text-[12px]">
                <span className="text-ink-mute">
                  {g.weekdays.length > 0 ? formatWeekdays(g.weekdays, true) : 'განრიგი არ არის'}
                  {g.weekdays.length > 0 && ` · ${g.startTime}`}
                </span>
                <span className="tabular flex items-center gap-1 font-medium text-ink-soft">
                  <Users className="size-3.5" />
                  {g._count.enrollments}
                </span>
              </div>

              {g.lecturers.length > 0 && (
                <p className="mt-2 truncate text-[12px] text-ink-mute">
                  {g.lecturers.map((l) => l.user.fullName).join(', ')}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}

      <GroupModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        group={null}
        onCreated={(id) => navigate(`/groups/${id}`)}
      />
    </>
  );
}
